import { Field, Column } from '../types';
import { isoToDatetimeLocal, toTimeInput, secondsToInterval } from './timestampUtils';

interface InitialFormDataResult {
    initialData: Record<string, any>;
    initialDisplayValues: Record<string, string>;
    initialRawValues: Record<string, any>;
}

export const buildInitialFormData = (
    fields: Field[],
    rowData: any,
    getFilter: (context: string) => any,
    columns?: Column[]
): InitialFormDataResult => {
    const initialData: Record<string, any> = {};
    const initialDisplayValues: Record<string, string> = {};
    const initialRawValues: Record<string, any> = {};

    fields.forEach(field => {
        if (field.context) {
            const contextValue = getFilter(field.context);
            if (contextValue) {
                initialData[field.code] = Number(contextValue);
            }
        } else if (rowData[field.code] !== undefined && rowData[field.code] !== null) {
            const rawValue = rowData[field.code];

            if ((field.type === 'many-to-one' || field.type === 'user' || field.type === 'created-by' || field.type === 'updated-by') && typeof rawValue === 'object' && rawValue !== null) {
                initialData[field.code] = rawValue.id;
                initialRawValues[field.code] = rawValue;

                // targetColumn.columnName decides WHAT to display: dynamic form
                // fields carry it directly, classic form fields via screen column
                let targetColumnName: string | undefined = field.targetColumn?.columnName;
                if (!targetColumnName && field.screenColumnId && columns) {
                    const screenColumn = columns.find(col => col.id === field.screenColumnId);
                    targetColumnName = screenColumn?.targetColumn?.columnName;
                }

                // Try to get display value in this order:
                // 1. targetColumn.columnName (the configured display column)
                // 2. field.code (common case where field code matches object property)
                // 3. name property
                // 4. email (user tables without a name)
                // 5. id as last resort
                // Note: We check !== undefined && !== null to handle 0 values correctly
                let displayValue = '';
                if (targetColumnName && rawValue[targetColumnName] !== undefined && rawValue[targetColumnName] !== null) {
                    displayValue = rawValue[targetColumnName];
                } else if (rawValue[field.code] !== undefined && rawValue[field.code] !== null) {
                    displayValue = rawValue[field.code];
                } else if (rawValue.name !== undefined && rawValue.name !== null) {
                    displayValue = rawValue.name;
                } else if (rawValue.full_name !== undefined && rawValue.full_name !== null) {
                    displayValue = rawValue.full_name;
                } else if (rawValue.email !== undefined && rawValue.email !== null) {
                    displayValue = rawValue.email;
                } else if (rawValue.id !== undefined && rawValue.id !== null) {
                    displayValue = rawValue.id;
                }
                initialDisplayValues[field.code] = displayValue !== '' ? String(displayValue) : String(rawValue.id);
            } else if (Array.isArray(rawValue)) {
                initialData[field.code] = rawValue;
            } else if (field.type === 'timestamp') {
                // Convert timestamp from ISO to datetime-local format for form input
                initialData[field.code] = isoToDatetimeLocal(rawValue);
                initialDisplayValues[field.code] = String(rawValue);
            } else if (field.type === 'time') {
                // Convert time to HH:mm format for form input
                initialData[field.code] = toTimeInput(rawValue);
                initialDisplayValues[field.code] = String(rawValue);
            } else if (field.type === 'interval') {
                // Convert duration (seconds) to HH:MM for form input
                initialData[field.code] = secondsToInterval(rawValue);
                initialDisplayValues[field.code] = secondsToInterval(rawValue);
            } else if (!['file', 'image'].includes(field.type) && typeof rawValue === 'object' && rawValue !== null && !Array.isArray(rawValue) && rawValue.id !== undefined && !rawValue.mimeType) {
                // Object with id (e.g., many-to-one data returned as object regardless of field type)
                // Exclude file objects (they have mimeType)
                initialData[field.code] = rawValue.id;
                initialDisplayValues[field.code] = String(rawValue.name || rawValue.id);
                initialRawValues[field.code] = rawValue;
            } else {
                initialData[field.code] = rawValue;
                initialDisplayValues[field.code] = String(rawValue);
            }
        }
    });

    return { initialData, initialDisplayValues, initialRawValues };
};

export const buildContextFields = (
    fields: Field[],
    getFilter: (context: string) => any
): Record<string, any> => {
    const contextData: Record<string, any> = {};

    fields.forEach(field => {
        if (field.context) {
            const contextValue = getFilter(field.context);
            if (contextValue) {
                contextData[field.code] = Number(contextValue);
            }
        }
    });

    return contextData;
};

export const buildCreateFormInitialData = (
    fields: Field[],
    getFilter: (context: string) => any
): Record<string, any> => {
    const initialData: Record<string, any> = {};

    fields.forEach(field => {
        if (field.context) {
            const contextValue = getFilter(field.context);
            if (contextValue) {
                initialData[field.code] = Number(contextValue);
            }
        } else if (field.defaultValue != null && field.defaultValue !== '') {
            // Pre-fill the column default so the user sees it and the stored
            // record matches. Relation and date/time defaults are left to the
            // database (SQL expressions and picker display don't map to inputs).
            const prefill = convertDefaultValue(field.type, field.defaultValue);
            if (prefill !== undefined) {
                initialData[field.code] = prefill;
            } else if (field.type === 'boolean' && field.required) {
                initialData[field.code] = false;
            }
        } else if (field.type === 'boolean' && field.required) {
            initialData[field.code] = false;
        }
    });

    return initialData;
};

const convertDefaultValue = (type: string, raw: string): any => {
    switch (type) {
        case 'boolean':
            return ['true', '1', 't'].includes(raw.toLowerCase());
        case 'integer':
            return Number.isNaN(parseInt(raw, 10)) ? undefined : parseInt(raw, 10);
        case 'float':
        case 'number':
            return Number.isNaN(parseFloat(raw)) ? undefined : parseFloat(raw);
        case 'string':
        case 'text':
        case 'markdown':
        case 'select':
            return raw;
        default:
            return undefined;
    }
};
